import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  CenterLoading,
  type DataDictionaryUrlPathMessage,
  IFrameMessageTypes,
  type MethodologyThemeMessage,
  PagePaths,
  sendMessageToIFrame,
  useListenForIFrameMessages,
} from '@revelio/core';
import { useChartTheme } from '@revelio/replots';

import {
  APP_PAGE_TYPE,
  usePageVisitTracking,
} from '../../hooks/usePageVisitTracking';
import { useBrand } from '../../shared/branding/brand-provider';
import {
  buildDataDictionaryIframeUrl,
  resolveDataDictionaryOrigin,
  toDashboardRoute,
} from './data-dictionary-url';
import styles from './iframed-pages.module.css';

const DATA_DICTIONARY_IFRAME_ID = 'ddId';
const DATA_DICTIONARY_ACTIVE_ORIGIN = resolveDataDictionaryOrigin();
// Only the embedded site may steer the dashboard route.
const DATA_DICTIONARY_MESSAGE_ORIGINS = [DATA_DICTIONARY_ACTIVE_ORIGIN];

// The site owns its location and the dashboard route mirrors it; only a
// route change made on the dashboard's side moves the iframe.
const DataDictionary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBrand();
  const { theme } = useChartTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loadCount, setLoadCount] = useState(0);
  const route = `${location.pathname}${location.search}`;

  // Brand and theme seed the first load; live theme changes go by postMessage.
  const [initialUrl] = useState(() =>
    buildDataDictionaryIframeUrl({
      pathname: location.pathname,
      search: location.search,
      clientName: brand.clientName,
      theme,
      origin: DATA_DICTIONARY_ACTIVE_ORIGIN,
    })
  );

  // The dashboard route of the page the iframe is showing.
  const iframeRouteRef = useRef(route);
  // The brand the iframe was last loaded with.
  const iframeBrandRef = useRef(brand.clientName);

  usePageVisitTracking({
    page: 'Data Dictionary',
    pageId: PagePaths.DATA_DICTIONARY,
    pageType: APP_PAGE_TYPE,
  });

  // Dashboard-initiated navigation. `replace` keeps the iframe out of the
  // browser history, which the dashboard route already records.
  useEffect(() => {
    const nextRoute = `${location.pathname}${location.search}`;
    if (nextRoute === iframeRouteRef.current) {
      return;
    }
    iframeRouteRef.current = nextRoute;
    iframeRef.current?.contentWindow?.location.replace(
      buildDataDictionaryIframeUrl({
        pathname: location.pathname,
        search: location.search,
        origin: DATA_DICTIONARY_ACTIVE_ORIGIN,
      })
    );
  }, [location.pathname, location.search]);

  // The brand arrives with the user and changes on impersonation; the site
  // reads it from the URL, so a change after mount reloads the current page.
  useEffect(() => {
    if (brand.clientName === iframeBrandRef.current) {
      return;
    }
    iframeBrandRef.current = brand.clientName;
    iframeRef.current?.contentWindow?.location.replace(
      buildDataDictionaryIframeUrl({
        pathname: location.pathname,
        search: location.search,
        clientName: brand.clientName,
        theme,
        origin: DATA_DICTIONARY_ACTIVE_ORIGIN,
      })
    );
  }, [brand.clientName, location.pathname, location.search, theme]);

  // Site-initiated navigation: mirror it into the dashboard route.
  useListenForIFrameMessages(
    IFrameMessageTypes.DataDictionaryUrlPath,
    ({ data, type }) => {
      const { path, search } = data as DataDictionaryUrlPathMessage;
      if (!path || typeof path !== 'string' || path === '/') {
        console.error('useListenForIFrameMessages: invalid path', {
          data: path,
          type,
        });
        return;
      }
      const target = toDashboardRoute(path, search);
      iframeRouteRef.current = target;
      if (target !== route) {
        navigate(target);
      }
    },
    { allowedOrigins: DATA_DICTIONARY_MESSAGE_ORIGINS }
  );

  // Hand the resolved colour mode to the site after every load (in-site
  // navigations are full page loads) and on every live Appearance change.
  useEffect(() => {
    if (loadCount === 0 || !iframeRef.current) {
      return;
    }
    const data: MethodologyThemeMessage = { theme };
    sendMessageToIFrame({
      iframe: iframeRef.current,
      data,
      type: IFrameMessageTypes.DataDictionaryTheme,
      domain: DATA_DICTIONARY_ACTIVE_ORIGIN,
    });
  }, [loadCount, theme]);

  return (
    <div className={styles.container}>
      <iframe
        ref={iframeRef}
        id={DATA_DICTIONARY_IFRAME_ID}
        title="Data Dictionary"
        src={initialUrl}
        className={styles.iframe}
        onLoad={() => setLoadCount((count) => count + 1)}
      />
      {loadCount === 0 && <CenterLoading className={styles.loader} />}
    </div>
  );
};

export default DataDictionary;
